import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/dateFormat';

const DealDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeal();
  }, [id]);

  const fetchDeal = async () => {
    try {
      const { data } = await axios.get(`/api/crm/deals/${id}`);
      setDeal(data);
    } catch (error) {
      toast.error('Failed to fetch deal');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!deal) return <div className="p-6">Deal not found</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/crm/deals')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Deals
          </button>
          <h1 className="text-3xl font-bold">{deal.dealName}</h1>
        </div>
        <button
          onClick={() => navigate(`/crm/deals/${id}/edit`)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Edit Deal
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-500">Deal Name</label>
            <p className="text-lg font-semibold">{deal.dealName}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Stage</label>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
              deal.stage === 'Closed Won' ? 'bg-green-100 text-green-800' :
              deal.stage === 'Closed Lost' ? 'bg-red-100 text-red-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {deal.stage}
            </span>
          </div>
        </div>

        {deal.description && (
          <div>
            <label className="block text-sm font-medium text-gray-500">Description</label>
            <p className="mt-1">{deal.description}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-500">Amount</label>
            <p className="text-lg font-semibold">${deal.amount || 0}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Probability</label>
            <p className="text-lg font-semibold">{deal.probability}%</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Expected Close</label>
            <p className="text-lg font-semibold">
              {deal.expectedCloseDate ? formatDate(deal.expectedCloseDate) : 'Not set'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {deal.lead && (
            <div>
              <label className="block text-sm font-medium text-gray-500">Related Lead</label>
              <p className="text-lg font-semibold">{deal.lead.name}</p>
            </div>
          )}
          {deal.owner && (
            <div>
              <label className="block text-sm font-medium text-gray-500">Owner</label>
              <p className="text-lg font-semibold">{deal.owner.firstName} {deal.owner.lastName}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
          <div>
            <label className="block text-sm font-medium text-gray-500">Created</label>
            <p>{formatDate(deal.createdAt)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Last Updated</label>
            <p>{formatDate(deal.updatedAt)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealDetails;